'use client';

import {useEffect} from 'react';
import {User, UserFlowStage} from './UserFlow';
import {logger} from './logger';

export interface UseSessionGuardProps {
    stage: UserFlowStage;
    user: User;
    submissionResult: { success: boolean; userUid?: string; error?: unknown } | null;
    isNavigating: boolean;
    reset: () => void;
}

/**
 * Hook that guards against stale or corrupted session states.
 *
 * Guards:
 * 1. Mid-flow without nfcTagUid: Redirect to idle if on a mid-flow page but no nfcTagUid exists
 * 2. Idle with stale data: Reset store if on idle page but nfcTagUid still exists from previous session
 * 3. Post-submission navigation: Reset and redirect if submissionResult exists but not on ThankYou page
 */
export function useSessionGuard({stage, user, submissionResult, isNavigating, reset}: UseSessionGuardProps): void {
    useEffect(() => {
        // Skip all guards while navigation is pending
        if (isNavigating) return;

        // Guard 1: Idle page with stale session data - reset the store
        if (stage === UserFlowStage.Idle && user.nfcTagUid) {
            logger.warn('Session guard: stale nfcTagUid found on idle page, resetting store', {
                nfcTagUid: user.nfcTagUid,
            });
            reset();
            return;
        }

        // Guard 2: Post-submission navigation back to non-ThankYou page
        if (submissionResult && stage !== UserFlowStage.ThankYou) {
            logger.warn('Session guard: submissionResult exists on non-ThankYou page, resetting', {
                stage,
                submissionSuccess: submissionResult.success,
            });
            reset();
            return;
        }

        // Guard 3: Mid-flow page without nfcTagUid - redirect to idle
        if (stage !== UserFlowStage.Idle && !user.nfcTagUid) {
            logger.warn('Session guard: no nfcTagUid found on mid-flow page, redirecting to idle', {
                stage,
            });
            reset();
        }
    }, [stage, user.nfcTagUid, submissionResult, isNavigating, reset]);
}
